import React, { useEffect, useRef, useState } from 'react';
import Products2Hero from '../components/Products2Hero';
import aboutStoryImg from '../assets/about-story.png';
import { PackageCheck, ShieldCheck, Wrench, BadgeCheck } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Helmet } from 'react-helmet-async';
import './AboutUsPage.css';

gsap.registerPlugin(ScrollTrigger);

const AboutUsPage = () => {
  const sectionRef = useRef(null);
  const ethosRef = useRef(null);
  const headingRef = useRef(null);
  const [headingVisible, setHeadingVisible] = useState(false);

  const splitWords = (text, startDelay = 0) => {
    const words = text.trim().split(/\s+/);
    return words.map((word, i) => (
      <span
        key={`${word}-${i}`}
        className="word-animate"
        style={{ animationDelay: `${startDelay + i * 40}ms` }}
      >
        {word}
        {i < words.length - 1 ? ' ' : ''}
      </span>
    ));
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('aos-hidden');
            entry.target.classList.add('aos-visible');
          }
        });
      },
      { threshold: 0.15 }
    );
    const items = sectionRef?.current?.querySelectorAll('.aos-item');
    items?.forEach((el) => observer?.observe(el));
    
    return () => observer?.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.12 }
    );

    const els = ethosRef.current?.querySelectorAll('.animate-on-scroll');
    if (els?.length) {
      els.forEach((el) => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHeadingVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    if (headingRef.current) observer.observe(headingRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!ethosRef.current) return;
    const ctx = gsap.context(() => {
      gsap.set('[data-p-line]', { scaleX: 0 });
      gsap.to('[data-p-line]', {
        scaleX: 1, duration: 1.4, ease: 'power2.out',
        scrollTrigger: { trigger: ethosRef.current, start: 'top 65%' },
      });
      gsap.fromTo('.process-step', { y: 70, opacity: 0 }, {
        y: 0, opacity: 1, duration: 0.75, stagger: 0.14, ease: 'power3.out',
        scrollTrigger: { trigger: ethosRef.current, start: 'top 65%' },
      });
    }, ethosRef);
    return () => ctx.revert();
  }, []);

  const ethosSteps = [
    { 
      num: '01', 
      title: 'Direct Sourcing', 
      desc: 'We source high-end corporate machines directly from enterprise environments, ensuring quality and longevity from the start.', 
      icon: <PackageCheck size={20} />, 
      accent: false 
    },
    { 
      num: '02', 
      title: '72-Point Inspection', 
      desc: 'Every device undergoes a rigorous hardware and internal audit at our Hyderabad facility before being certified for sale.', 
      icon: <ShieldCheck size={20} />, 
      accent: true 
    },
    { 
      num: '03', 
      title: 'Professional Restore', 
      desc: 'Our technicians deeply clean and restore the hardware, replacing thermal paste and ensuring peak performance.', 
      icon: <Wrench size={20} />, 
      accent: false 
    },
    { 
      num: '04', 
      title: 'Verified Performance', 
      desc: 'A final certification step ensures the OS is clean and the unit is ready for its new professional owner with a 6-month warranty.', 
      icon: <BadgeCheck size={20} />, 
      accent: true 
    }
  ];

  return (
    <div className="about-us-page">
      <Helmet>
        <title>About Us | Nexawave Technology</title>
        <meta name="description" content="Welcome to Nexawave Technology. Discover quality refurbished laptops, refurbished desktops, and premium refurbished monitors at the best prices in Hyderabad." />
        <meta name="keywords" content="about nexawave, refurbished laptops, refurbished desktops, premium refurbished monitors, affordable used laptops" />
      </Helmet>
      <Products2Hero 
        className="hero-tight"
        tagline="OUR VISION · OUR VALUES · OUR COMMITMENT"
        title1="About Us"
        title2=""
        title3=""
        description=""
        button1Text="Our Values"
        button1Link="#values"
        button2Text="Contact Us"
        button2Link="/contact-us"
      />

      <section id="nosotros" ref={sectionRef} className="py-16 px-6 md:px-10 max-w-7xl mx-auto about-story-grid">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image collage */}
          <div className="relative h-[500px] aos-item aos-hidden">
            {/* Main image */}
            <div
              className="absolute inset-0 rounded-2xl overflow-hidden border border-gold/20"
              style={{ right: '20%', bottom: '15%' }}>
              
              <img
                src={aboutStoryImg}
                alt="Nexawave team collaborating in Hyderabad office"
                className="w-full h-full object-cover"
                style={{ filter: 'brightness(0.95) saturate(1.1)' }} />
              
              <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(0,99,184,0.1) 0%, transparent 60%)' }} />
            </div>
            {/* Floating stat card */}
            <div
              className="absolute bottom-8 right-0 stat-card-floating p-5 w-52 shadow-xl"
              style={{ zIndex: 10 }}>
              
              <div className="font-montserrat text-3xl font-bold text-obsidian mb-1">2024</div>
              <div className="text-xs text-obsidian/60 uppercase tracking-wider">Founded in Hyderabad</div>
              <div className="mt-3 pt-3 border-t border-blue-accent/20">
                <div className="font-montserrat text-xl font-bold text-obsidian">50+</div>
                <div className="text-xs text-obsidian/60 uppercase tracking-wider">Expert Employees</div>
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="aos-item aos-hidden" style={{ transitionDelay: '0.15s' }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-6 h-px bg-blue-accent/50" />
              <span className="text-xs uppercase tracking-widest text-blue-accent font-bold">Our Story</span>
            </div>

            <h2 className="font-montserrat text-4xl md:text-5xl text-obsidian leading-tight mb-6 font-extrabold">
              From a Humble Beginning<br />
              <span className="italic text-blue-accent">to a Powerful Vision.</span>
            </h2>

            <div className="space-y-5 border-l-4 border-blue-accent/20 pl-6 mb-8">
              <p className="text-zinc-text-dark text-base leading-relaxed">
                NexaWave's journey began in 2024, nestled in the vibrant city of Hyderabad. What started as a bold dream shared among a small group of visionary minds quickly blossomed into a mission.
              </p>
              <p className="text-zinc-text-dark text-base leading-relaxed">
                Today, NexaWave stands as a testament to that initial spark of ingenuity. We have grown into a dedicated team of 50 employees, all fueled by an unwavering belief in technology's power to transform lives and connect communities.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-6 pt-6 border-t border-gray-100">
              {[
                { num: '2024', label: 'Established', sub: 'Hyderabad' },
                { num: 'Hyderabad', label: 'Origin City', sub: 'India' },
                { num: 'Global', label: 'Vision', sub: 'Worldwide' }
              ].map((stat, i) => (
                <div key={i} className="stat-unit">
                  <div className="font-montserrat text-2xl font-bold text-blue-accent">{stat?.num}</div>
                  <div className="text-sm font-semibold text-obsidian mt-0.5">{stat?.label}</div>
                  <div className="text-xs text-zinc-text-dark italic">{stat?.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="values" ref={ethosRef} className="py-16 px-6 md:px-10 max-w-7xl mx-auto">
        <div className='mb-12'>
          <div className="flex items-center gap-4 mb-6">
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-accent">The Nexawave Ethos</span>
            <div style={{ width: '40px', height: '1px', background: 'var(--blue-accent)', opacity: 0.2 }} />
          </div>
          
          <h2 
            ref={headingRef}
            className={`section-fade ${headingVisible ? 'visible' : ''} text-4xl md:text-6xl font-black leading-[0.95] tracking-tight text-[#1A1A1A]`}
          >
            {splitWords('We Are A Trusted', 100)}
            <br />
            <em className="text-blue-accent" style={{ fontStyle: 'normal' }}>
              {splitWords('Refurbished Seller.', 220)}
            </em>
          </h2>
          
          <p className="mt-8 text-[#555555] text-lg max-w-xl leading-relaxed font-medium animate-on-scroll anim-in delay-300">
            From our Hyderabad facility to your doorstep, we follow a rigorous 4-step restoration process to ensure every device performs like new.
          </p>
        </div>

        <div className='relative hidden lg:block mb-8'>
          <div className='h-px w-full overflow-hidden bg-gray-200'>
            <div data-p-line className='h-full w-full origin-left bg-blue-accent' />
          </div>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
          {ethosSteps.map((step, idx) => (
            <div key={step.num} className='process-step card opacity-0 p-7 md:p-8 flex flex-col gap-5 relative overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-sm'>
              <div className='flex items-center justify-between'>
                <div className='w-10 h-10 rounded-xl flex items-center justify-center'
                  style={{ background: step.accent ? 'var(--blue-accent)' : '#f3f4f6', color: step.accent ? '#FFFFFF' : '#6b7280' }}>
                  {step.icon}
                </div>
                <span className='text-4xl font-black leading-none select-none'
                  style={{ color: 'var(--blue-accent)', opacity: step.accent ? 0.45 : 0.1 }}>
                  {step.num}
                </span>
              </div>
              <div>
                <h3 className='text-lg font-black mb-2 text-balance text-obsidian'>{step.title}</h3>
                <p className='text-sm leading-relaxed text-zinc-text-dark text-pretty'>{step.desc}</p>
              </div>
              {step.accent && (
                <div className='absolute top-0 right-0 w-[4px] h-full bg-blue-accent opacity-100' />
              )}
              {/* Right side single vertical line for box 2 and 4 - Like Type */}
              {(idx === 1 || idx === 3) && (
                <div 
                  className="absolute right-[8px] top-1/2 -translate-y-1/2"
                  style={{ width: '1.5px', height: '40px', background: '#0063B8', opacity: 0.15 }} 
                />
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AboutUsPage;
